"use client"
import { memo, useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowRight, Timer, Sparkles, ChevronLeft, ChevronRight, Star, X } from 'lucide-react';
import Image from 'next/image';
import { animations } from '@/app/styles/design-system';
import Section from '@/components/layout/Section';
import Link from 'next/link';
import { getSpecials } from '@/app/lib/sanity';

const ImageOnlySpecial = memo(({ item, index, isActive, onImageClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      variants={animations.fadeInUp}
      className="absolute inset-0 w-full"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: isActive ? 1 : 0,
        scale: isActive ? 1 : 0.9,
        pointerEvents: isActive ? 'auto' : 'none'
      }}
      transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }}
    >
      <div className="relative min-h-[400px] sm:h-[400px] flex items-center justify-center bg-transparent py-8 sm:py-0">
        <div className="relative w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center px-4">
          {/* Text content */}
          <div className="text-white space-y-4 sm:space-y-6 text-center md:text-left order-2 md:order-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-2 sm:gap-3 justify-center md:justify-start"
            >
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-1.5 bg-white/95 text-primary-orange px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg"
              >
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-twinkle" />
                <span>Special Offer</span>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-1.5 bg-primary-orange/20 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium"
              >
                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-white" />
                <span>Featured</span>
              </motion.div>
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold px-4 md:px-0"
            >
              {item.name.split(' ').map((word, i) => (
                <motion.span
                  key={i}
                  className="inline-block"
                  whileHover={{ scale: 1.05, rotate: i % 2 ? 2 : -2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {word}{' '}
                </motion.span>
              ))}
            </motion.h3>

            {item.description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
                transition={{ delay: 0.4 }}
                className="text-white/80 text-base sm:text-lg max-w-xl mx-auto md:mx-0 px-4 md:px-0"
              >
                {item.description}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 items-center justify-center md:justify-start"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onImageClick(item)}
                className="cursor-pointer"
              >
                <Button 
                  className="bg-white hover:bg-white/90 text-primary-orange font-medium px-6 sm:px-8 py-2.5 sm:py-3 rounded-full transition-all duration-300 hover:shadow-xl transform-gpu hover:-translate-y-0.5 group text-sm sm:text-base relative overflow-hidden"
                >
                  <motion.span className="inline-flex items-center gap-2 relative z-10">
                    View Details
                    <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
                  </motion.span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary-orange/0 via-primary-orange/10 to-primary-orange/0"
                    animate={{
                      x: ["0%", "200%"],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </Button>
              </motion.div>
              <motion.div 
                className="flex items-center gap-2 text-white/80"
                whileHover={{ scale: 1.05 }}
              >
                <Timer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm">
                  Ends {new Date(item.endDate).toLocaleDateString()}
                </span>
              </motion.div>
            </motion.div>
          </div>
            
          {/* Image container with Circle Frame */}
          <div className="relative w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] mx-auto order-1 md:order-2 cursor-pointer"
               onClick={() => onImageClick(item)}>
            {/* Circle Frame Container */}
            <motion.div 
              className="absolute inset-0"
              initial={{ rotate: -10, scale: 0.9 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              {/* Solid White Circle Frame with enhanced shadow */}
              <motion.div 
                className="absolute inset-0 bg-white rounded-full shadow-[0_8px_32px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm"
                transition={{ type: "spring", stiffness: 300 }}
              />
              
              {/* Decorative rings */}
              <motion.div 
                className="absolute -inset-2 rounded-full border-2 border-white/20"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute -inset-4 rounded-full border border-white/10"
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              />
              
              {/* Image Container */}
              <div className="absolute inset-[16px] sm:inset-[20px] rounded-full overflow-hidden bg-gradient-to-br from-[#FFF6F0] to-white">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                  animate={{ 
                    opacity: isActive ? 1 : 0, 
                    scale: isActive ? 1 : 0.8, 
                    rotate: isActive ? 0 : -10 
                  }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="relative w-full h-full flex items-center justify-center group"
                >
                  <motion.div
                    animate={!prefersReducedMotion ? { 
                      y: [0, -10, 0],
                      rotate: [0, -2, 2, 0]
                    } : {}}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="relative w-full h-full flex items-center justify-center"
                  >
                    <div className="relative w-[90%] h-[90%] flex items-center justify-center">
                      <Image
                        src={item.imageUrl}
                        alt={item.name}
                        fill
                        className="object-contain transform-gpu will-change-transform group-hover:scale-110 transition-transform duration-500"
                        sizes="(max-width: 640px) 280px, (max-width: 768px) 380px, 420px"
                        priority={index === 0}
                        onLoad={() => setImageLoaded(true)}
                        style={{
                          objectFit: item.imageUrl.includes('transparent') || item.imageUrl.includes('png') ? 'contain' : 'cover',
                          padding: item.imageUrl.includes('transparent') || item.imageUrl.includes('png') ? '8%' : '0'
                        }}
                      />
                    </div>
                    {/* Enhanced shine effect */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 rotate-12"
                      animate={{
                        x: ["-100%", "200%"],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 1
                      }}
                    />
                  </motion.div>
                </motion.div>
              </div>

              {/* Add sparkle effects */}
              {imageLoaded && !prefersReducedMotion && [...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-4 h-4"
                  style={{
                    top: `${20 + i * 30}%`,
                    left: `${80 + i * 5}%`,
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    rotate: [0, 180],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.6,
                    ease: "easeInOut",
                  }}
                >
                  <div className="w-full h-full relative">
                    <div className="absolute inset-0 rotate-45 bg-white rounded-sm" />
                    <div className="absolute inset-0 -rotate-45 bg-white rounded-sm" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

ImageOnlySpecial.displayName = 'ImageOnlySpecial';

const SpecialItem = memo(({ item, index, isActive, onImageClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  // If this is an image-only special, use the specialized component
  if (item.displayType === 'imageOnly') {
    return <ImageOnlySpecial item={item} index={index} isActive={isActive} onImageClick={onImageClick} />;
  }

  return (
    <motion.div
      variants={animations.fadeInUp}
      className="absolute inset-0 w-full"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ 
        opacity: isActive ? 1 : 0,
        scale: isActive ? 1 : 0.9,
        pointerEvents: isActive ? 'auto' : 'none'
      }}
      transition={{
        duration: 0.5,
        ease: [0.23, 1, 0.32, 1]
      }}
    >
      <div className="relative min-h-[400px] sm:h-[400px] flex items-center justify-center bg-transparent py-8 sm:py-0">
        <div className="relative w-full max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center px-4">
          {/* Text content */}
          <div className="text-white space-y-4 sm:space-y-6 text-center md:text-left order-2 md:order-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-2 sm:gap-3 justify-center md:justify-start"
            >
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-1.5 bg-white/95 text-primary-orange px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium shadow-lg"
              >
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-twinkle" />
                <span>Limited Time Offer</span>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-1.5 bg-primary-orange/20 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium"
              >
                <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-white" />
                <span>Featured Deal</span>
              </motion.div>
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold px-4 md:px-0"
            >
              {item.name.split(' ').map((word, i) => (
                <motion.span
                  key={i}
                  className="inline-block"
                  whileHover={{ scale: 1.05, rotate: i % 2 ? 2 : -2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {word}{' '}
                </motion.span>
              ))}
            </motion.h3>

            {item.description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
                transition={{ delay: 0.4 }}
                className="text-white/80 text-base sm:text-lg max-w-xl mx-auto md:mx-0 px-4 md:px-0"
              >
                {item.description}
              </motion.p>
            )}

            {/* Only show pricing for menu item specials */}
            {item.originalPrice && item.discountedPrice && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
                transition={{ delay: 0.5 }}
                className="flex items-baseline gap-2 sm:gap-4 justify-center md:justify-start flex-wrap"
              >
                <motion.span 
                  className="text-base sm:text-lg text-white/70 line-through"
                  whileHover={{ scale: 1.1 }}
                >
                  ${item.originalPrice.toFixed(2)}
                </motion.span>
                <motion.span 
                  className="text-2xl sm:text-4xl font-bold text-white"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ${item.discountedPrice.toFixed(2)}
                </motion.span>
                <motion.span 
                  className="text-white/90 text-xs sm:text-sm px-2 py-1 bg-white/10 rounded-md"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                >
                  Save ${(item.originalPrice - item.discountedPrice).toFixed(2)}
                </motion.span>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 20 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-4 items-center justify-center md:justify-start"
            >
              <Link href="/menu">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    className="bg-white hover:bg-white/90 text-primary-orange font-medium px-6 sm:px-8 py-2.5 sm:py-3 rounded-full transition-all duration-300 hover:shadow-xl transform-gpu hover:-translate-y-0.5 group text-sm sm:text-base relative overflow-hidden"
                  >
                    <motion.span className="inline-flex items-center gap-2 relative z-10">
                      Order Now 
                      <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1" />
                    </motion.span>
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-primary-orange/0 via-primary-orange/10 to-primary-orange/0"
                      animate={{
                        x: ["0%", "200%"],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    />
                  </Button>
                </motion.div>
              </Link>
              <motion.div 
                className="flex items-center gap-2 text-white/80"
                whileHover={{ scale: 1.05 }}
              >
                <Timer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm">
                  Ends {new Date(item.endDate).toLocaleDateString()}
                </span>
              </motion.div>
            </motion.div>
          </div>
            
          {/* Image container with Circle Frame */}
          <div className="relative w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] mx-auto order-1 md:order-2">
            {/* Circle Frame Container */}
            <motion.div 
              className="absolute inset-0"
              initial={{ rotate: -10, scale: 0.9 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Solid White Circle Frame with enhanced shadow */}
              <motion.div 
                className="absolute inset-0 bg-white rounded-full shadow-[0_8px_32px_-4px_rgba(0,0,0,0.1)] backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              
              {/* Decorative rings */}
              <motion.div 
                className="absolute -inset-2 rounded-full border-2 border-white/20"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div 
                className="absolute -inset-4 rounded-full border border-white/10"
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              />
              
              {/* Image Container */}
              <div className="absolute inset-[16px] sm:inset-[20px] rounded-full overflow-hidden bg-gradient-to-br from-[#FFF6F0] to-white">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                  animate={{ 
                    opacity: isActive ? 1 : 0, 
                    scale: isActive ? 1 : 0.8, 
                    rotate: isActive ? 0 : -10 
                  }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="relative w-full h-full flex items-center justify-center"
                >
                  <motion.div
                    animate={!prefersReducedMotion ? { 
                      y: [0, -10, 0],
                      rotate: [0, -2, 2, 0]
                    } : {}}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="relative w-full h-full flex items-center justify-center"
                  >
                    <div className="relative w-[90%] h-[90%] flex items-center justify-center">
                      <Image
                        src={item.imageUrl}
                        alt={item.name}
                        fill
                        className="object-contain transform-gpu will-change-transform hover:scale-110 transition-transform duration-500"
                        sizes="(max-width: 640px) 280px, (max-width: 768px) 380px, 420px"
                        priority={index === 0}
                        onLoad={() => setImageLoaded(true)}
                        style={{
                          objectFit: item.imageUrl.includes('transparent') || item.imageUrl.includes('png') ? 'contain' : 'cover',
                          padding: item.imageUrl.includes('transparent') || item.imageUrl.includes('png') ? '8%' : '0'
                        }}
                      />
                    </div>
                    {/* Enhanced shine effect */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/30 to-white/0 rotate-12"
                      animate={{
                        x: ["-100%", "200%"],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 1
                      }}
                    />
                  </motion.div>
                </motion.div>
              </div>

              {/* Enhanced percentage badge - only for menu item specials */}
              {item.originalPrice && item.discountedPrice && (
                <motion.div 
                  className="absolute -right-2 sm:-right-4 top-4 bg-primary-orange text-white w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] rounded-full flex items-center justify-center font-bold shadow-lg"
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 12 }}
                  transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                  whileHover={{ 
                    scale: 1.1, 
                    rotate: 0,
                    transition: { type: "spring", stiffness: 300 }
                  }}
                >
                  <div className="text-center leading-tight">
                    <span className="text-xl sm:text-2xl">
                      {Math.round((1 - item.discountedPrice / item.originalPrice) * 100)}%
                    </span>
                    <br />
                    <span className="text-[10px] sm:text-xs">OFF</span>
                  </div>
                </motion.div>
              )}

              {/* Add sparkle effects */}
              {imageLoaded && !prefersReducedMotion && [...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-4 h-4"
                  style={{
                    top: `${20 + i * 30}%`,
                    left: `${80 + i * 5}%`,
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    rotate: [0, 180],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.6,
                    ease: "easeInOut",
                  }}
                >
                  <div className="w-full h-full relative">
                    <div className="absolute inset-0 rotate-45 bg-white rounded-sm" />
                    <div className="absolute inset-0 -rotate-45 bg-white rounded-sm" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

SpecialItem.displayName = 'SpecialItem';

const TodaysSpecials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const prefersReducedMotion = useReducedMotion();
  
  const handleImageClick = useCallback((item) => {
    setModalImage(item);
  }, []);

  const closeModal = useCallback(() => {
    setModalImage(null);
  }, []);

  const handleBackdropClick = useCallback((e) => {
    // Only close if clicking directly on the backdrop, not on child elements
    if (e.target === e.currentTarget) {
      closeModal();
    }
  }, [closeModal]);

  const fetchSpecials = useCallback(async () => {
    try {
      const data = await getSpecials();
      setSpecials(data || []);
    } catch (error) {
      console.error('Error fetching specials:', error);
      setSpecials([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpecials();
  }, [fetchSpecials]);

  // Auto-advance carousel
  useEffect(() => {
    if (prefersReducedMotion || !specials.length) return;
    
    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % specials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [specials.length, prefersReducedMotion]);

  if (!loading && specials.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <Section className="relative flex flex-col py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-orange to-primary-light rounded-full mb-4"></div>
            <p className="text-white/80">Loading today's specials...</p>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <>
      {/* Full-screen image modal */}
      <AnimatePresence>
        {modalImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={handleBackdropClick}
          >
            {/* Image container - only takes up space needed for image */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-[90vw] max-h-[90vh]"
              style={{ 
                width: 'auto', 
                height: 'auto',
                aspectRatio: 'auto'
              }}
            >
              <Image
                src={modalImage.imageUrl}
                alt={modalImage.name}
                width={1200}
                height={800}
                className="object-contain max-w-[90vw] max-h-[90vh] w-auto h-auto"
                sizes="90vw"
                priority
              />
              
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200 z-10"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
            
            {/* View Menu button - positioned outside image container */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
              <Link href="/menu">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    className="bg-primary-orange hover:bg-primary-orange/90 text-white font-medium px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl transform-gpu hover:-translate-y-0.5 group shadow-2xl"
                  >
                    <span className="inline-flex items-center gap-3 text-lg">
                      View Full Menu
                      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Button>
                </motion.div>
              </Link>
            </div>
            
            {/* Click outside hint - only show briefly */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 1, 0] }}
              transition={{ 
                times: [0, 0.2, 0.8, 1],
                duration: 4,
                delay: 1
              }}
              className="absolute top-8 left-1/2 -translate-x-1/2 bg-black/50 text-white/80 px-4 py-2 rounded-lg backdrop-blur-sm text-sm text-center cursor-pointer"
              onClick={closeModal}
            >
              Click anywhere to close
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regular specials section */}
    <Section 
      className="relative flex flex-col py-8 sm:py-16 overflow-hidden bg-gradient-to-br from-[#E67300] to-[#FF8C1A]"
    >
      <div className="absolute inset-0">
        {/* Enhanced background patterns */}
        <div className="absolute inset-0">
          {/* Primary gradient with more vibrant colors */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#E67300] via-[#FF8C1A] to-[#FF6B35]" />
          
          {/* Large gradient orbs */}
          <div className="absolute -top-1/4 -left-1/4 w-[800px] h-[800px] bg-gradient-to-br from-white/20 to-transparent rounded-full blur-[80px] animate-float-slow" />
          <div className="absolute -bottom-1/4 -right-1/4 w-[800px] h-[800px] bg-gradient-to-tl from-white/20 to-transparent rounded-full blur-[80px] animate-float-slow-reverse" />
          
          {/* Layered patterns */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.2),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.15),transparent_70%)]" />
          
          {/* Decorative elements */}
          <div className="absolute inset-0">
            {/* Themed floating elements */}
            <div className="absolute top-10 left-[10%] text-5xl opacity-40 animate-float-themed">
              🍩
            </div>
            <div className="absolute top-[40%] right-[5%] text-4xl opacity-30 animate-float-themed-reverse">
              🧋
            </div>
            <div className="absolute bottom-20 left-[15%] text-3xl opacity-20 animate-float-themed-delay">
              ✨
            </div>
            
            {/* Enhanced sparkle effects */}
            <div className="absolute inset-0">
              {/* Camera flash effects in corners */}
              <div className="absolute -top-20 -right-20 w-80 h-80 animate-flash-top-right">
                <div className="absolute inset-0 bg-gradient-to-br from-white via-white/80 to-transparent rounded-full blur-2xl transform rotate-45" />
              </div>
              <div className="absolute -bottom-20 -left-20 w-80 h-80 animate-flash-bottom-left">
                <div className="absolute inset-0 bg-gradient-to-tl from-white via-white/80 to-transparent rounded-full blur-2xl transform -rotate-45" />
              </div>

              {/* Star-shaped sparkles */}
              {[...Array(12)].map((_, i) => (
                <div
                  key={`star-sparkle-${i}`}
                  className="absolute animate-star-sparkle"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`
                  }}
                >
                  <div className="w-4 h-4 relative transform rotate-45">
                    <div className="absolute inset-0 bg-white/30 rounded-sm animate-star-pulse" />
                    <div className="absolute inset-0 bg-white/30 rounded-sm transform rotate-90" />
                  </div>
                </div>
              ))}

              {/* Glowing orbs */}
              {[...Array(15)].map((_, i) => (
                <div
                  key={`glow-${i}`}
                  className="absolute animate-glow-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 4}s`
                  }}
                >
                  <div className="w-3 h-3 bg-white/20 rounded-full blur-[2px]" />
                  <div className="absolute inset-0 bg-white/20 rounded-full blur-[4px] animate-glow" />
                </div>
              ))}

              {/* Sparkle trails */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={`trail-${i}`}
                  className="absolute animate-trail"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`
                  }}
                >
                  <div className="relative">
                    {[...Array(3)].map((_, j) => (
                      <div
                        key={`trail-particle-${j}`}
                        className="absolute w-2 h-2 bg-white/20 rounded-full blur-[1px]"
                        style={{
                          top: j * -8,
                          opacity: 1 - j * 0.3,
                          transform: `scale(${1 - j * 0.2})`
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Diagonal stripes */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                linear-gradient(
                  45deg,
                  rgba(255,255,255,0.1) 25%,
                  transparent 25%,
                  transparent 50%,
                  rgba(255,255,255,0.1) 50%,
                  rgba(255,255,255,0.1) 75%,
                  transparent 75%,
                  transparent
                )
              `,
              backgroundSize: '100px 100px'
            }}
          />

          {/* Dot grid pattern */}
          <div 
            className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
                radial-gradient(circle at center, rgba(255,255,255,0.2) 1px, transparent 1px),
                radial-gradient(circle at center, rgba(255,255,255,0.2) 1px, transparent 1px)
              `,
              backgroundSize: '30px 30px, 90px 90px'
            }}
          />

          {/* Light rays */}
          <div className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,transparent_0deg,rgba(255,255,255,0.15)_90deg,transparent_180deg,transparent_360deg)] animate-spin-very-slow opacity-30" />
        </div>

        {/* Side decorations */}
        <div className="absolute -left-20 top-1/2 -translate-y-1/2 w-40 h-[600px]">
          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-full blur-2xl transform rotate-12 animate-pulse-slow" />
        </div>
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-40 h-[600px]">
          <div className="absolute inset-0 bg-gradient-to-l from-white/10 to-transparent rounded-full blur-2xl transform -rotate-12 animate-pulse-slow" />
          </div>
      </div>

      {/* Add new keyframes for enhanced animations */}
      <style jsx global>{`
        @keyframes float-themed {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes float-themed-reverse {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-5deg); }
        }

        @keyframes float-themed-delay {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }

        .animate-float-themed {
          animation: float-themed 6s ease-in-out infinite;
        }

        .animate-float-themed-reverse {
          animation: float-themed-reverse 7s ease-in-out infinite;
        }

        .animate-float-themed-delay {
          animation: float-themed-delay 8s ease-in-out infinite;
        }

        @keyframes star-sparkle {
          0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1) rotate(180deg); opacity: 0.3; }
        }

        @keyframes star-pulse {
          0% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.5); opacity: 0.2; }
          100% { transform: scale(2); opacity: 0; }
        }

        @keyframes glow-pulse {
          0%, 100% { transform: scale(0.8); opacity: 0.2; }
          50% { transform: scale(1.2); opacity: 0.3; }
        }

        @keyframes glow {
          0% { transform: scale(1); opacity: 0.3; }
          100% { transform: scale(2.5); opacity: 0; }
        }

        @keyframes trail {
          0% { transform: translateY(0) scale(0); opacity: 0; }
          50% { transform: translateY(-50px) scale(1); opacity: 0.3; }
          100% { transform: translateY(-100px) scale(0); opacity: 0; }
        }

        /* Enhanced flash animations */
        @keyframes flash {
          0%, 100% { opacity: 0; transform: scale(0.8) rotate(0deg); }
          50% { opacity: 0.3; transform: scale(1.3) rotate(180deg); }
        }

        .animate-flash-top-right {
          animation: flash 3s ease-in-out infinite;
          animation-delay: 0s;
        }

        .animate-flash-bottom-left {
          animation: flash 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>

      <div className="relative container mx-auto">
        {/* Enhanced Section Header */}
        <motion.div
          variants={animations.fadeInUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-16 px-4"
        >
          <motion.div 
            className="inline-block relative mb-4 sm:mb-6"
          >
            <div className="inline-flex items-center gap-2 text-primary-orange font-medium bg-white/95 backdrop-blur-sm px-6 py-2.5 rounded-full text-base border border-white/20 shadow-lg">
              <span>✨</span>
              <span>Specials</span>
            </div>
            {/* Decorative elements for the badge */}
            <div className="absolute -inset-1.5 bg-gradient-to-r from-white/50 to-white/20 rounded-full blur-sm -z-10" />
            <div className="absolute -inset-3 bg-gradient-to-r from-white/30 to-white/10 rounded-full blur-md -z-20" />
          </motion.div>

          <motion.div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 sm:mb-6 relative z-10 px-4">
              <span className="inline-block bg-gradient-to-br from-white via-white to-white/90 bg-clip-text text-transparent pb-2">
                Specials
              </span>
              <motion.div
                className="absolute -right-2 sm:-right-6 -top-2 sm:-top-6"
                animate={{ rotate: [0, 15, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="w-8 h-8 sm:w-12 sm:h-12 relative">
                  <div className="absolute inset-0 bg-white rounded-full blur-sm opacity-20 animate-pulse-slow" />
                  <div className="absolute inset-0 flex items-center justify-center text-white text-xl sm:text-2xl">
                    ✨
                  </div>
                </div>
              </motion.div>
            </h2>
            
            {/* Enhanced subtitle with gradient and glow */}
            <div className="relative">
              <p className="font-body text-base sm:text-lg md:text-xl max-w-2xl mx-auto px-4">
                <span className="bg-gradient-to-r from-white/90 via-white/80 to-white/90 bg-clip-text text-transparent">
                  Don't miss out on these exclusive offers, available for a limited time only
                </span>
              </p>
              {/* Subtle glow effect under the text */}
              <div className="absolute -inset-x-4 top-1/2 h-1/2 bg-gradient-to-b from-white/20 to-transparent blur-xl -z-10" />
            </div>
          </motion.div>
        </motion.div>

        {/* Update background gradients for better flow */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-orange via-[#FF7A4D] to-[#FF6B35] opacity-90 bg-gradient-animate" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.2),transparent_50%)]" />
        </div>

        {/* Carousel Container */}
        <div className="relative min-h-[400px] sm:h-[400px] bg-transparent">
          <AnimatePresence mode="wait">
            {specials.map((item, index) => (
              <SpecialItem 
                key={item._id}
                item={item}
                index={index}
                isActive={index === activeIndex}
                onImageClick={handleImageClick}
              />
            ))}
          </AnimatePresence>

          {/* Enhanced Navigation Dots */}
          <div className="absolute -bottom-4 sm:bottom-0 left-1/2 -translate-x-1/2 flex items-center gap-2 sm:gap-3">
            {specials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`group relative w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                  index === activeIndex 
                    ? 'bg-white scale-125' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              >
                <span className="absolute -inset-2 rounded-full bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300" />
              </button>
            ))}
          </div>

          {/* Enhanced Navigation Arrows */}
          {specials.length > 1 && (
            <>
              <button
                onClick={() => setActiveIndex((current) => (current - 1 + specials.length) % specials.length)}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110 group"
                aria-label="Previous slide"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:-translate-x-1" />
              </button>
              <button
                onClick={() => setActiveIndex((current) => (current + 1) % specials.length)}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110 group"
                aria-label="Next slide"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 transition-transform group-hover:translate-x-1" />
              </button>
            </>
          )}
        </div>
      </div>
    </Section>
    </>
  );
};

export default TodaysSpecials; 
